import {Component, OnInit, Input, Output, EventEmitter, OnChanges, OnDestroy} from '@angular/core';
import {DialogService} from '@rbaip/ui-component';
import {formatDate} from '@angular/common';
import {Subscription} from 'rxjs';
import { MatStepper } from '@angular/material/stepper';
import {
  Asset, assetInspectionTypeName, assetMessage,
  AssetSaleInfo,
  assetSaleTypeName,
  AuditAssetMove, equipmentState, MOVE_ASSET_INTO_AUCTION_EVENT,
  MOVE_ASSET_INTO_OTHER_CONTRACT,
  MOVE_ASSET_INTO_OTHER_OPPORTUNITY, RBA_LIVE, RETURN_TO_PREVIOUS_SALE, INVENTORY_CONTROL
} from '../../../shared/domain/item';
import {AuctionEvent} from '../../../shared/domain/auction-event';
import {ItemSummaryAssetsService} from '../../../shared/asset/item-summary-assets.service';
import {ItemSummaryAssetControlGroupService} from '../../../shared/asset-package/item-summary-asset-control-group.service';
import {Opportunity, OpportunityContract, OpportunityType} from '../../../shared/domain/opportunity';
import {ItemSummaryEventService} from '../../../shared/auction-event/item-summary-event.service';
import {ItemSummaryEventServiceImpl} from '../../../shared/auction-event/item-summary-event-impl.service';
import {ASSET_STATUS_LOT_PULLED} from "../../../shared/domain/item/asset-status-type";
import {CONSIGNMENT_OPPORTUNITY, INDUSTRIAL_OPPORTUNITY,MULTICHANNEL_OPPORTUNITY} from "../../../shared/domain/item/opportunity-record-type.const";
import { SALESHUB_CONTRACT_CHANNEL } from '../../../shared/domain';

@Component({
  selector: 'lib-item-summary-asset-move-sale-info',
  templateUrl: './asset-move-sale-info.component.html',
  styleUrls: ['./asset-move-sale-info.component.scss'],
  providers: [{provide: ItemSummaryEventService, useClass: ItemSummaryEventServiceImpl}]
})
export class AssetMoveSaleInfoComponent implements OnInit, OnChanges, OnDestroy {

  @Input() auditAssetMove: AuditAssetMove;
  @Input() stepper: MatStepper;
  @Input() assets: Asset[];
  @Output() loadMoveReason = new EventEmitter();

  public assetSaleInfo: AssetSaleInfo;
  public displayPrices = false;
  public currentLegalAccountName: string;
  public targetLegalAccountName: string;
  public targetSaleType: string;
  public targetEventName: string;
  public targetLocation: string;
  public targetInspectionType: string;
  public saleInfoSelection: string;
  public displayNewSaleInfo = true;
  public displayCurrentInspection = true;
  public displayTargetInspection = true;
  public displayCurrentSaleValues = false;
  public MOVE_ASSET_INTO_OTHER_CONTRACT = MOVE_ASSET_INTO_OTHER_CONTRACT;
  public MOVE_ASSET_INTO_OTHER_OPPORTUNITY = MOVE_ASSET_INTO_OTHER_OPPORTUNITY;
  public readonly useCurrentSaleInfo = 'useCurrent';
  public readonly useTargetSaleInfo = 'useTarget';
  public readonly MIXED_VALUES = 'Mixed Values';

  private currentSaleType: string;
  private currentEventId: number;
  private currentAuctionStartDate: Date;
  private currentSiteId: number;
  private currentStorageLocationId: number;
  private currentStorageLocation: string;
  private currentInspectionType: string;
  private targetSaleInfo: AssetSaleInfo;
  private targetSiteId: number;
  private targetStorageLocationId: number;
  private currentAuctionEvent: AuctionEvent;

  private targetAssetControlGroupSubscription: Subscription;
  private targetAuctionEventSubscription: Subscription;
  private operationalEntitySubscription: Subscription;

  constructor(private dialogService: DialogService,
              private assetsService: ItemSummaryAssetsService,
              private assetControlGroupService: ItemSummaryAssetControlGroupService,
              private eventService: ItemSummaryEventService) {
    this.subscribeTargetAuctionEvent();
  }

  ngOnInit() {
    this.loadSaleInfo();
    this.setCurrentSaleInfo();
    this.setTargetContractDefaults();
  }

  ngOnChanges(changes: any) {
    this.setTargetContractDefaults();
  }

  ngOnDestroy() {
    if (this.targetAssetControlGroupSubscription) {
      this.targetAssetControlGroupSubscription.unsubscribe();
    }
    if (this.targetAuctionEventSubscription) {
      this.targetAuctionEventSubscription.unsubscribe();
    }
    if (this.operationalEntitySubscription) {
      this.operationalEntitySubscription.unsubscribe();
    }
  }

  public isNextButtonEnabled(): boolean {
    if (this.assets.length > 1) {
      return (this.assetSaleInfo.isValidForMultipleAssets() || !!this.saleInfoSelection);
    } else {
      return (this.assetSaleInfo.isValid() || !!this.saleInfoSelection);
    }
  }

  public getCurrentSaleTypeName(): string {
    if (this.currentSaleType == this.MIXED_VALUES) {
      return this.MIXED_VALUES;
    } else {
      return assetSaleTypeName[this.currentSaleType];
    }
  }

  public getCurrentAuctionEventName(): string {
    let currentAuctionEventName;
    if (this.currentEventId) {
      if (this.currentEventId == -1) {
        currentAuctionEventName = this.MIXED_VALUES;
      } else if (this.currentSaleType == RBA_LIVE) {
        this.assetSaleInfo.rbAuctionEvents.forEach(auctionEvent => {
          if (this.currentEventId == auctionEvent.auctionEventId) {
            currentAuctionEventName = auctionEvent.saleNumber + ' - ' + auctionEvent.eventName;
            if (!auctionEvent.allowsIpInspection) {
              this.displayCurrentInspection = false;
            }
          }
        });
        // Below is needed to display Holding Pen event
        if (!currentAuctionEventName && !!this.currentAuctionEvent) {
          currentAuctionEventName = this.currentAuctionEvent.eventName;
        }
      } else {
        currentAuctionEventName = formatDate(this.currentAuctionStartDate, 'MMM dd', 'en');
      }
    }
    return currentAuctionEventName;
  }

  public getCurrentLocation(): string {
    let location;
    if (this.currentSiteId && this.currentStorageLocationId) {
      location = this.MIXED_VALUES;
    } else if (this.currentSiteId) {
      if (this.currentSiteId == -1) {
        location = this.MIXED_VALUES;
      } else {
        this.assetSaleInfo.siteList.forEach(site => {
          if (this.currentSiteId == site.id) {
            location = 'RBA Site: ' + site.cityProvinceCountry;
          }
        });
      }
    } else if (this.currentStorageLocationId) {
      if (this.currentStorageLocationId == -1) {
        location = this.MIXED_VALUES;
      } else {
        this.assetSaleInfo.assetControlGroupLocations.forEach(assetLocation => {
          if (this.currentStorageLocationId == assetLocation.id) {
            location = assetLocation.oneLineFullAddress;
          }
        });
      }
    } else if (this.currentStorageLocation) {
      location = this.currentStorageLocation;
    } else {
      location = this.MIXED_VALUES;
    }
    return location;
  }

  public getCurrentInspectionType(): string {
    if (this.currentInspectionType == this.MIXED_VALUES) {
      return this.MIXED_VALUES;
    } else {
      return assetInspectionTypeName[this.currentInspectionType];
    }
  }

  public getTargetLocation(): string {
    let location = 'N/A';
    if (this.targetSiteId) {
      this.assetSaleInfo.siteList.forEach(site => {
        if (this.targetSiteId == site.id) {
          location = 'RBA Site: ' + site.cityProvinceCountry;
        }
      });
    } else if (this.targetStorageLocationId) {
      location = this.targetLocation;
    }
    return location;
  }

  public onSaleInfoSelection(): void {
    this.displayNewSaleInfo = false;
  }

  public onNewSaleInfoDisplay(): void {
    this.displayNewSaleInfo = true;
    this.saleInfoSelection = null;
  }

  public isTargetDefaultsEnabled(): boolean {
    if (!this.targetSaleType || !this.getTargetLocation() || !this.targetInspectionType) {
      return false;
    } else if (this.targetSaleType && this.targetSaleType == RBA_LIVE && !this.targetEventName) {
      return false;
    } else {
      return true;
    }
  }

  public continueToNextStep(): void {
    let isOpportunityOrContractCurrencyDifferent = false;

    if (this.targetSaleInfo && this.targetSaleInfo.opportunityCurrency && this.assetSaleInfo.opportunityCurrency
      && this.targetSaleInfo.opportunityCurrency !== this.assetSaleInfo.opportunityCurrency) {
      isOpportunityOrContractCurrencyDifferent = true;
    }

    if (this.saleInfoSelection) {
      this.auditAssetMove.retainSaleInfo = 'Y';
      if (this.saleInfoSelection == this.useTargetSaleInfo) {
        this.setAssetsWithSaleInfo(this.targetSaleInfo, !isOpportunityOrContractCurrencyDifferent);
      }
    } else {
      this.auditAssetMove.retainSaleInfo = 'N';
      this.setAssetsWithSaleInfo(this.assetSaleInfo, !isOpportunityOrContractCurrencyDifferent);
    }
    let targetOpportunityId = this.auditAssetMove.currentAssetControlGroup.opportunityId;
    if (this.auditAssetMove.destinationType == MOVE_ASSET_INTO_OTHER_CONTRACT || this.auditAssetMove.destinationType == MOVE_ASSET_INTO_OTHER_OPPORTUNITY) {
      const targetOpportunityContract = this.auditAssetMove.selectedContract ? this.auditAssetMove.selectedContract : this.auditAssetMove.selectedOpportunity;
      targetOpportunityId = targetOpportunityContract.opportunityId;
    }
    if (this.isSaleInfoRequired()) {
      this.getOperationalEntity(targetOpportunityId, false);
    } else {
      this.goToNextStep();
    }
    this.getOperationalEntity(targetOpportunityId, false);
  }

  private loadSaleInfo(): void {
    this.assetSaleInfo = new AssetSaleInfo();
    this.assetSaleInfo.opportunityId = this.auditAssetMove.currentAssetControlGroup.opportunityId;
    this.assetSaleInfo.assetControlGroupId = this.auditAssetMove.currentAssetControlGroup.acgId;
    const opportunityContract = this.auditAssetMove.selectedContract ? this.auditAssetMove.selectedContract : this.auditAssetMove.selectedOpportunity;
    if (!!opportunityContract) {
      this.assetSaleInfo.opportunityId = opportunityContract.opportunityId;
    }
    this.assetSaleInfo.highlightRequiredField = this.isSaleInfoRequired();
    this.assetSaleInfo.isEditable = true;
    this.assetSaleInfo.displayPrices = this.assets.length === 1;
    this.assetSaleInfo.displayListingFee = false;
    this.assetSaleInfo.displaySetDefaultCheckBox = false;
    this.assetSaleInfo.displayBudget = false;
    this.assetSaleInfo.isAuctionSite = true;
    if (this.assets.length > 0 && (this.auditAssetMove.destinationType === MOVE_ASSET_INTO_AUCTION_EVENT || this.auditAssetMove.destinationType == RETURN_TO_PREVIOUS_SALE )) {
      const asset = this.assets[0];
      this.assetSaleInfo.saleType = (asset.saleType !== INVENTORY_CONTROL) ? asset.saleType : null;
      this.assetSaleInfo.platformName = (asset.saleType !== INVENTORY_CONTROL) ? asset.platformName : null;
      this.assetSaleInfo.equipId = asset.equipId;
      this.assetSaleInfo.siteId = asset.siteId;
      this.assetSaleInfo.yardDeliveyDate = asset.yardDeliveyDt;
      this.assetSaleInfo.isAuctionSite = (asset.siteId != null);
      this.assetSaleInfo.auctionEventId = asset.auctionEventId;
      this.assetSaleInfo.locationId = asset.locationId;
      this.assetSaleInfo.isRBRealm = asset.isRBAsset();
      this.assetSaleInfo.equipmentStatus = asset.equipmentStatus;
      this.assetSaleInfo.equipmentStatusName = asset.equipmentStatusName;
      this.assetSaleInfo.shouldBeDisabledDuringMove = asset.equipmentStatus >= equipmentState.inspectionPerformed;
      this.assetSaleInfo.appraisalLocationId = asset.appraisalLocationId;
      let movingLotPulledAssets : boolean = asset.assetStatusId === ASSET_STATUS_LOT_PULLED;

      if ( this.assets.length === 1 ) {
        this.assetSaleInfo.sellerReserveAmt = asset.sellerReserveAmt;
        this.assetSaleInfo.strikePriceAmt = asset.strikePriceAmt;
        this.assetSaleInfo.shootPrice =  asset.shootPrice;
        this.assetSaleInfo.inspectionType = asset.inspectionType;
      } else {
        this.assets.forEach(asset => {
          if (this.assetSaleInfo.saleType !== asset.saleType) {
            this.assetSaleInfo.saleType = null;
          }
          if (this.assetSaleInfo.auctionEventId !== asset.auctionEventId) {
            this.assetSaleInfo.auctionEventId = null;
          }
          if (this.assetSaleInfo.siteId !== asset.siteId) {
            this.assetSaleInfo.siteId = null;
          }
          if (this.assetSaleInfo.locationId !== asset.locationId) {
            this.assetSaleInfo.locationId = null;
          }
          if (this.assetSaleInfo.appraisalLocationId !== asset.appraisalLocationId) {
            this.assetSaleInfo.appraisalLocationId = null;
          }
          if (this.assetSaleInfo.yardDeliveyDate !== asset.yardDeliveyDt) {
            this.assetSaleInfo.yardDeliveyDate = null;
          }
          if (this.assetSaleInfo.isRBRealm !== asset.isRBAsset()) {
            this.assetSaleInfo.isRBRealm = false;
          }
          if (asset.assetStatusId !== ASSET_STATUS_LOT_PULLED) {
            movingLotPulledAssets = false;
          }
        });
      }

      this.assetSaleInfo.areLocationsSame = !asset.appraisalLocationId || (asset.locationId === asset.appraisalLocationId);

      if ( movingLotPulledAssets && this.assetSaleInfo.isRBRealm ){
        let eventStartDate = new Date( asset.pulledFromAuctionStartDate );
        let currentDate = new Date();
        if ( eventStartDate.getTime() > currentDate.getTime() ){
          this.assetSaleInfo.auctionEventId = asset.pulledFromAuctionEventId;
        } else {
          if  ( this.auditAssetMove.currentAssetControlGroup ){
            this.assetSaleInfo.auctionEventId = this.auditAssetMove.currentAssetControlGroup.defaultAuctionEventId;
          }
        }
        this.assetSaleInfo.saleType = RBA_LIVE;
      }
    }
  }

  private setCurrentSaleInfo(): void {
    this.assets.forEach(asset => {
      if (!this.currentSaleType) {
        this.currentSaleType = asset.saleType;
      } else if (this.currentSaleType != asset.saleType) {
        this.currentSaleType = this.MIXED_VALUES;
      }

      if (!this.currentEventId) {
        this.currentEventId = asset.auctionEventId;
        this.currentAuctionStartDate = asset.prevEndDt;
      } else if (this.currentEventId != asset.auctionEventId) {
        this.currentEventId = -1;
      } else if (this.currentAuctionStartDate != asset.prevEndDt) {
        this.currentEventId = -1;
      }

      /*Assuming all selected assets will be either equips/assets, cannot be a combination.
        Also, assuming that equip's site/seller location is updated all the time.
       */
      if (asset.siteId) {
        if (!this.currentSiteId) {
          this.currentSiteId = asset.siteId;
        } else if (this.currentSiteId != asset.siteId) {
          this.currentSiteId = -1;
        }
      } else if (asset.assetLocationDto) {
        if (!this.currentStorageLocationId) {
          this.currentStorageLocationId = asset.assetLocationDto.id;
        } else if (this.currentStorageLocationId != asset.locationId) {
          this.currentStorageLocationId = -1;
        }
      }

      if (!this.currentInspectionType) {
        this.currentInspectionType = asset.inspectionType;
      } else if (this.currentInspectionType != asset.inspectionType) {
        this.currentInspectionType = this.MIXED_VALUES;
      }
    });

    if (!!this.currentEventId && this.currentEventId !== -1) {
      this.eventService.getAuctionEventDetailsObservable(this.currentEventId)
        .subscribe(response => {
          if (response && response.auctionEventDto) {
            this.currentAuctionEvent = response.auctionEventDto;
          }
        });
    }
    this.currentLegalAccountName = this.auditAssetMove.currentOpportunity?.legalAccountName;
  }

  private setTargetContractDefaults(): void {
    const opportunityContract = this.auditAssetMove.selectedContract ? this.auditAssetMove.selectedContract : this.auditAssetMove.selectedOpportunity;
    if (!opportunityContract || !opportunityContract.assetControlGroupId) {
      return;
    }
    this.dialogService.showProgress();
    this.determineIfCurrentSaleValuesCanBeDisplayed();
    const assetControlGroupId = opportunityContract.assetControlGroupId;
    this.targetLegalAccountName = opportunityContract.legalName;
    this.targetAssetControlGroupSubscription = this.assetControlGroupService.getAssetPackageInfo(assetControlGroupId).subscribe(assetControlGroup => {
      if (assetControlGroup) {
        this.auditAssetMove.targetAssetControlGroup = assetControlGroup;
        this.targetSaleInfo = AssetSaleInfo.getSaleInfoFromAssetControlGroup(assetControlGroup);
        this.targetSaleType = assetControlGroup.defaultSaleType ? assetSaleTypeName[assetControlGroup.defaultSaleType] : '';
        if (assetControlGroup.defaultAuctionEventId && assetControlGroup.defaultSaleType && assetControlGroup.defaultSaleType == RBA_LIVE) {
          this.eventService.getAuctionEventDetails(assetControlGroup.defaultAuctionEventId);
        }
        this.targetSiteId = assetControlGroup.defaultSiteId;
        this.targetStorageLocationId = assetControlGroup.locationId;
        this.targetInspectionType = assetControlGroup.defaultInspectionType ? assetInspectionTypeName[assetControlGroup.defaultInspectionType] : '';
        if (assetControlGroup.sellerLocation && assetControlGroup.sellerLocation.addressDto) {
          this.targetLocation = assetControlGroup.sellerLocation.addressDto.oneLineFullAddress;
        }
        this.dialogService.hideProgress();
      }
    });
  }

  /**
   * If we are moving IC/Holding pen or Lot Pulled assets, we need not to show current sale values as an option
   */
  private checkIfMovingHoldingPenOrLotPulledAssets() {
    const inventoryControlOrLotPulledAssets = this.assets.filter(asset => asset.saleType === INVENTORY_CONTROL || asset.assetStatusId === ASSET_STATUS_LOT_PULLED);
    if (inventoryControlOrLotPulledAssets && inventoryControlOrLotPulledAssets.length > 0) {
      this.displayCurrentSaleValues = false;
    }
  }

  private determineIfCurrentSaleValuesCanBeDisplayed(): void {
    const targetOpportunityContract = this.auditAssetMove.selectedContract ? this.auditAssetMove.selectedContract : this.auditAssetMove.selectedOpportunity;
    if (!!targetOpportunityContract) {
      //Opportunity Related Conditions
      switch (targetOpportunityContract.opportunityType) {
        case OpportunityType.MULTICHANNEL: {
          this.displayCurrentSaleValues = true;
          break;
        } case OpportunityType.IRONPLANET: {
          const rbAssets = this.assets.filter(asset => asset.saleType === RBA_LIVE);
          const hasRbAssets = !!rbAssets && rbAssets.length > 0;
          this.displayCurrentSaleValues = !hasRbAssets;
          break;
        } case OpportunityType.CONSIGNMENT_OPPORTUNITY: {
          if (!targetOpportunityContract.contractChannel) this.displayCurrentSaleValues = true;
          const contractChannel = targetOpportunityContract.contractChannel;
          switch (contractChannel) {
            case SALESHUB_CONTRACT_CHANNEL.live: {
              this.displayCurrentSaleValues = !this.assets.some(asset => asset.saleType !== RBA_LIVE);
              break;
            } case SALESHUB_CONTRACT_CHANNEL.online: {
              this.displayCurrentSaleValues = !this.assets.some(asset => asset.saleType === RBA_LIVE);
              break;
            } default: {
              this.displayCurrentSaleValues = true;
              break;
            }
          }
          break;
        } default: {
          const ipAssets = this.assets.filter(asset => asset.saleType !== RBA_LIVE);
          const hasIpAssets = !!ipAssets && ipAssets.length > 0;
          this.displayCurrentSaleValues = !hasIpAssets;
          break;
        }
      }
      //Contract Related Conditions
      this.checkIfMovingHoldingPenOrLotPulledAssets();
    }
    if (this.displayCurrentSaleValues) {
      this.getOperationalEntity(targetOpportunityContract.opportunityId, true);
    }
  }

  private subscribeTargetAuctionEvent(): void {
    this.targetAuctionEventSubscription = this.eventService.auctionEventCallback.subscribe(
      (response) => {
        const auctionEvent = response;
        this.targetEventName = auctionEvent.saleNumber + ' - ' + auctionEvent.eventName;
        if (!auctionEvent.allowsIpInspection) {
          this.displayTargetInspection = false;
        }
      });
  }

  private setAssetsWithSaleInfo(assetSaleInfo: AssetSaleInfo, retainAssetShootPrice: boolean): void {
    this.assets.forEach(asset => {
    let shootPrice = 0;
    let algoPrice = 0;
    let marketValue = 0;

    if (retainAssetShootPrice) {
        shootPrice = asset.shootPrice;
        algoPrice = asset.algoPriceAmt;
        marketValue = asset.marketValue;
      }

    AssetSaleInfo.resetSaleInfoForAsset(asset);
    assetSaleInfo.setAssetWithSaleInfo(asset);

    if (retainAssetShootPrice) {
        asset.shootPrice = shootPrice;
        asset.algoPriceAmt = algoPrice;
        asset.marketValue = marketValue;
      }
    });
  }

  private getOperationalEntity(opportunityId: number, isCurrentSaleValuesCheck: boolean): void {
    this.operationalEntitySubscription = this.assetsService.checkOperationalEntity(opportunityId, this.assets).subscribe(
      (response) => {
        if (response && response.error) {
          if (isCurrentSaleValuesCheck) {
            this.displayCurrentSaleValues = false;
          } else {
            this.dialogService.showMessageDialog(assetMessage.assetMoveOperationalEntityError);
            this.dialogService.hideProgress();
          }
        } else {
          this.assets = response;
          if (!isCurrentSaleValuesCheck) {
            this.goToNextStep();
          }
        }
      }, error => {
        this.dialogService.showMessage('Unexpected error occurred while validating Operational Entity. Please contact tech support. ');
        this.dialogService.hideProgress();
      });
  }

  private goToNextStep(): void {
    this.loadMoveReason.emit(this.assets);
    this.stepper.next();
  }

  private isSaleInfoRequired(): boolean {
    return this.auditAssetMove.destinationType != MOVE_ASSET_INTO_OTHER_OPPORTUNITY;
  }
}
